import type { FeatureFlag } from "@sunner-cloud/api-client";
import { createContext, use } from "react";

export const KNOWN_FLAGS = {
  HOME_MAP: "home_map_enabled",
  PLANT_DASHBOARD_V2: "plant_dashboard_v2_enabled",
  PLANT_DASHBOARD_V3: "plant_dashboard_v3_enabled",
  PLANT_LAYOUT: "plant_layout_enabled",
  PLANT_COMMISSIONING: "plant_commissioning_enabled",
  PLANT_CONFIGURATION_WIZARD: "plant_configuration_wizard_enabled",
  PLANT_CREATION: "enable_plant_creation",
  AVAILABILITY_ALERTS: "availability_alerts_enabled",
} as const;

export type FlagName =
  (typeof KNOWN_FLAGS)[keyof typeof KNOWN_FLAGS] | (string & {}); // intentional pattern to preserve IDE autocomplete for known flags

export interface FeatureFlagsContextValue {
  flags: FeatureFlag[];
  flagsMap: Map<string, boolean>;
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
  error: Error | null;
  isEnabled: (flagName: FlagName) => boolean;
}

const defaultContextValue: FeatureFlagsContextValue = {
  flags: [],
  flagsMap: new Map(),
  isLoading: true,
  isReady: false,
  isError: false,
  error: null,
  isEnabled: () => false,
};

export const FeatureFlagsContext =
  createContext<FeatureFlagsContextValue>(defaultContextValue);

export const useFeatureFlagsContext = (): FeatureFlagsContextValue => {
  const context = use(FeatureFlagsContext);

  if (context === defaultContextValue) {
    throw new Error(
      "useFeatureFlagsContext must be used within a FeatureFlagsProvider",
    );
  }

  return context;
};
